import { createClient } from '../../../server/client/client.no-deps'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { TimeoutError } from '../../../shared/lib/timeout.no-deps'
import { assumeExhaustion } from '../../../shared/lib/typeHelpers.no-deps'
import * as sentry from '../../src/utils/sentry'
import { Cale_uploadDeletedEvent, Cale_uploadNewEvent, Cale_uploadUpdatedEvent } from '../Cale/Cale.sync'
import type { Cust_ICustomer } from '../Cust/Cust.ICustomer'
import { DS_getData } from '../DS/DS.Data'
import type { Flow_ActionReport } from '../Flow/Flow.ActionReport'
import { mapToServerExhaustiveAnswer } from '../Flow/Flow.sync'
import { IDB_deviceCompletedData } from '../IDB/IDB.deviceDB'
import { IDB_flowActionReports, IDB_photos, IDB_update } from '../IDB/IDB.dexie'
import type { Imag_Photo } from '../Imag/Imag.Photo'
import { Logg_createLogger } from '../Logg/Logg.Logger'
import { Rpts_prepareReportsForUpload } from '../Rpts/Rpts.prepareReportsForUpload'
import type { NReports } from '../Rpts/Rpts.types'
import { type Serv_HttpRequestOptions, Serv_httpRequestWithOption } from '../Serv/Serv.httpRequest'
import { Serv_send } from '../Serv/Serv.socketClient'
import type { Type_ICalendarEvent, Type_ILocationTimelineEvent, Type_IndexedDbId } from '../Type/Type.hive'
import type { User_IUser } from '../User/User.IUser'
import { IDB_smartDatasReadAll } from '../IDB/IDB.table.smartData'
import type { SD_TSmartData } from '../SD/SD.types'
import { isNew, isDeleted, isUpdated } from '../../src/utils/sync'
import { IDB_locationTimelineEventsReadAll } from '../IDB/IDB.table.locationTimelineEvent'
import { IDB_calendarEventsReadAll } from '../IDB/IDB.table.calendarEvent'

export type Devi_CompletedData =
    | CompletedDataBase<'report', ReportWithCalendarEvent>
    | CompletedDataBase<'non-workflow-action-report', Flow_ActionReport>
    | CompletedDataBase<'photo', Imag_Photo>
    | CompletedDataBase<'smart-data', SD_TSmartData>
    | CompletedDataBase<'location-timeline-event', Type_ILocationTimelineEvent>
    | CompletedDataBase<'calendar-event', Type_ICalendarEvent>

type CompletedDataBase<T extends string, V> = {
    id?: number // Auto-generated
    userId: number
    accessToken: string
    customer: Cust_ICustomer
    type: T
    value: V
}

type ReportWithCalendarEvent = {
    report: NReports.TSubmittedReport
    calendarEvent: Type_ICalendarEvent
}

/**
 * Called during logout to save completed data to the device database to be uploaded after logout
 */
export async function Devi_saveCompletedData() {
    const logger = Logg_createLogger(`Devi_saveCompletedData`)
    try {
        const { accessToken, user, customer } = DS_getData()

        await saveReportsToDeviceDatabase(logger, accessToken, user, customer)

        await saveNonWorkflowReportsToDeviceDatabase(accessToken, user, customer)

        await saveLegacyPhotosToDeviceDatabase(accessToken, user, customer)

        await saveSmartDataToDeviceDatabase(accessToken, user, customer)

        await saveLocationTimelineEventsToDeviceDatabase(accessToken, user, customer)

        await saveCalendarEventsToDeviceDatabase(accessToken, user, customer)
    } catch (error) {
        logger.error(error)
    }
}

async function saveReportsToDeviceDatabase(
    logger: ClientLogger,
    accessToken: string,
    user: User_IUser,
    customer: Cust_ICustomer
) {
    // Prepare reports as if we were uploading them now
    const reports = await Rpts_prepareReportsForUpload(logger)

    // Save the reports to the device database
    await IDB_deviceCompletedData().bulkPut(
        reports.map(({ reportToSave, reportToSend }) => ({
            accessToken,
            userId: user.id,
            customer,
            type: 'report',
            value: {
                report: reportToSend,
                calendarEvent: reportToSave.event,
            },
        }))
    )

    // Fetch all the completed non workflow actions reports
    const nonWorkflowActions = await IDB_flowActionReports().where('status').equals('completed').toArray()

    // Save the non worklfow reports to the device database
    await IDB_deviceCompletedData().bulkPut(
        nonWorkflowActions.map((nonWorkflowAction) => ({
            accessToken,
            userId: user.id,
            customer,
            type: 'non-workflow-action-report',
            value: nonWorkflowAction,
        }))
    )
}

async function saveNonWorkflowReportsToDeviceDatabase(accessToken: string, user: User_IUser, customer: Cust_ICustomer) {
    // Fetch all the completed non workflow actions reports
    const nonWorkflowActions = await IDB_flowActionReports().where('status').equals('completed').toArray()

    // Save the non worklfow reports to the device database
    await IDB_deviceCompletedData().bulkPut(
        nonWorkflowActions.map((nonWorkflowAction) => ({
            accessToken,
            userId: user.id,
            customer,
            type: 'non-workflow-action-report',
            value: nonWorkflowAction,
        }))
    )
}

async function saveLegacyPhotosToDeviceDatabase(accessToken: string, user: User_IUser, customer: Cust_ICustomer) {
    // Fetch all the pendingUpload legacy photos
    const legacyPhotos = await IDB_photos().where('status').equals('pendingUpload').toArray()

    // Save the legacy photos to the device database
    await IDB_deviceCompletedData().bulkPut(
        legacyPhotos.map((legacyPhoto) => ({
            accessToken,
            userId: user.id,
            customer,
            type: 'photo',
            value: legacyPhoto,
        }))
    )
}

async function saveSmartDataToDeviceDatabase(accessToken: string, user: User_IUser, customer: Cust_ICustomer) {
    // Fetch smart data from local database
    const smartData = await IDB_smartDatasReadAll()

    // Filter smart data of type text with meta
    const textSmartData = smartData.filter((sm) => sm.type === 'text' && sm.meta)

    // Save smart data on device database
    await IDB_deviceCompletedData().bulkPut(
        textSmartData.map((data) => {
            return {
                accessToken,
                userId: user.id,
                customer,
                type: 'smart-data',
                value: data,
            }
        })
    )
}

async function saveLocationTimelineEventsToDeviceDatabase(
    accessToken: string,
    user: User_IUser,
    customer: Cust_ICustomer
) {
    // Fetch location timeline events from local database
    const locationTimelineEvents = await IDB_locationTimelineEventsReadAll()

    const newLocationTimelineEvents = locationTimelineEvents
        // Filter location timeline events which have atleast 1 new event
        .filter((timelineEvent) => {
            return timelineEvent.events.filter(isNew).length > 0
        })
        // Get only new events
        .map((timelineEvent) => {
            return { ...timelineEvent, events: timelineEvent.events.filter(isNew) }
        })

    // Save new location timeline events on device database
    await IDB_deviceCompletedData().bulkPut(
        newLocationTimelineEvents.map((data) => {
            return {
                accessToken,
                userId: user.id,
                customer,
                type: 'location-timeline-event',
                value: data,
            }
        })
    )
}

async function saveCalendarEventsToDeviceDatabase(accessToken: string, user: User_IUser, customer: Cust_ICustomer) {
    // Fetch all calendar events from local database
    const allcalendarEvents = await IDB_calendarEventsReadAll()

    // Filter calendar events with meta
    // as we need to check meta to either add, edit or delete calendar event
    const calendarEvents = allcalendarEvents.filter((event) => event.meta)

    // Save calendar events on device database
    await IDB_deviceCompletedData().bulkPut(
        calendarEvents.map((data) => {
            return {
                accessToken,
                userId: user.id,
                customer,
                type: 'calendar-event',
                value: data,
            }
        })
    )
}

/**
 * Called during device sync to upload complete data after logout
 */
export async function Devi_uploadCompletedData() {
    // Get a list of the ids for each row
    const { accessToken: currentToken, customer: currentCustomer, user: currentUser } = DS_getData()
    const ids = (await IDB_deviceCompletedData().orderBy('id').keys()) as number[]
    // This will hold ids of calendar events that have been uploaded with the report
    // and should not be uploaded again
    const duplicateCalendarEventsIds: Type_IndexedDbId[] = []

    for (const id of ids) {
        try {
            // Read the row from idb
            let row = await IDB_deviceCompletedData().get(id)

            // Work out if the current user is logged in
            const isCurrentlyLoggedIn = currentUser?.id === row.userId && currentCustomer?.id === row.customer?.id

            // If so, update the access token
            if (isCurrentlyLoggedIn) {
                row = { ...row, accessToken: currentToken }
                await IDB_update(IDB_deviceCompletedData(), id, { accessToken: currentToken })
            }

            const httpClient = createClient(
                Serv_httpRequestWithOption({ accessToken: row.accessToken, customer: row.customer })
            )

            // Create sendOptions to pass around
            const sendOptions: Serv_HttpRequestOptions = {
                accessToken: row.accessToken,
                customer: row.customer,
            }

            switch (row.type) {
                case 'report':
                    const { calendarEvent: reportCalendarEvent, report } = row.value as ReportWithCalendarEvent
                    const { onlineId } = await uploadCalendarEvent(reportCalendarEvent, sendOptions)
                    await Serv_send('v2.reports.add', { ...report, event: onlineId }, sendOptions)
                    await IDB_deviceCompletedData().delete(row.id)

                    // Check if the same report calendar event exists in local db
                    // and store it in an array, so that we can delete them after to avoid duplicate uploads
                    const calendarEventIds = (
                        await IDB_deviceCompletedData()
                            .orderBy('id')
                            .filter(
                                (data) => data.type === 'calendar-event' && data.value.id === reportCalendarEvent.id
                            )
                            .toArray()
                    ).map((data) => data.type === 'calendar-event' && data.value.id)

                    if (calendarEventIds.length) {
                        duplicateCalendarEventsIds.push(...calendarEventIds)
                    }

                    break
                case 'non-workflow-action-report':
                    const nonWorkflowActionReport = row.value

                    // Let typescript know that these action reports are completed
                    if (nonWorkflowActionReport.status !== 'completed') return

                    // Do not pass in null coordinates
                    const { lat, lon } = nonWorkflowActionReport.coordinates
                    const coordinates = { lat: lat || 0, lon: lon || 0 }

                    // Upload it to the server
                    await httpClient.confirmAction({
                        clientId: nonWorkflowActionReport.id,
                        coordinates,
                        scheduledInstance: nonWorkflowActionReport.scheduledInstance.id,
                        startDate: nonWorkflowActionReport.startDate,
                        endDate: nonWorkflowActionReport.completedDate,
                        answers: nonWorkflowActionReport.answers.map(mapToServerExhaustiveAnswer),
                        teamDoneFor: nonWorkflowActionReport.scheduledInstance.teams[0]?.id,
                    })

                    // Then delete it from device data
                    await IDB_deviceCompletedData().delete(row.id)
                    break
                case 'photo':
                    const photo = row.value

                    // Extract the photo content from data URI
                    const content = photo.dataURI.substring(photo.dataURI.indexOf(',') + 1)

                    // Try to upload
                    await Serv_send(
                        'file.put',
                        { fileName: photo.fileName, file: content, type: photo.extension },
                        sendOptions
                    )

                    await IDB_deviceCompletedData().delete(row.id)
                    break
                case 'smart-data':
                    const smartData = row.value

                    if (isNew(smartData) && smartData.meta.targets) {
                        const obj = {
                            key: smartData.key,
                            value: smartData.value,
                            type: smartData.type,
                            source: smartData.source,
                            // For the moment we assume that there is only one target in the array. We want to support
                            // mulitiple targets being stored in the local db but the server api doesn't deal with
                            // specifying multiple targets and we only have a use case for one at the moment.
                            targets: smartData.meta.targets[0],
                        }

                        // Add new smart data on server
                        await Serv_send('v2.smartData.add', obj, sendOptions)
                    }

                    if (isUpdated(smartData)) {
                        const obj = {
                            id: smartData.id as number,
                            key: smartData.key,
                            value: smartData.value,
                        }

                        // Update new smart data on server
                        await Serv_send('v2.smartData.edit', obj, sendOptions)
                    }

                    if (isDeleted(smartData) && smartData.meta.targets) {
                        // Delete smart data on the server
                        await Serv_send('v2.smartData.delete', { id: smartData.id as number }, sendOptions)
                    }

                    await IDB_deviceCompletedData().delete(row.id)
                    break
                case 'location-timeline-event':
                    const { events, id: timelineId } = row.value

                    for (const event of events) {
                        // Convert to format for server
                        const data = {
                            date: event.date,
                            type: event.type,
                            item: event.item,
                            location: timelineId,
                        }
                        await Serv_send('timeline.set', data as any)
                    }

                    await IDB_deviceCompletedData().delete(row.id)
                    break
                case 'calendar-event':
                    const calendarEvent = row.value
                    const alreadyUploaded = duplicateCalendarEventsIds.some((id) => calendarEvent.id === id)
                    if (alreadyUploaded) {
                        // delete the row if has been uploaded already
                        await IDB_deviceCompletedData().delete(row.id)
                    } else {
                        await uploadCalendarEvent(calendarEvent, sendOptions)
                        await IDB_deviceCompletedData().delete(row.id)
                    }
                    break
                default:
                    assumeExhaustion(row)
            }
        } catch (error) {
            if (error instanceof TimeoutError) continue
            sentry.simpleError(error)
        }
    }
}

async function uploadCalendarEvent(event: Type_ICalendarEvent, sendOptions: Serv_HttpRequestOptions) {
    if (event.meta.new === true || event.onlineId === undefined) {
        return Cale_uploadNewEvent(event, sendOptions)
    } else if (event.meta.updated === true) {
        await Cale_uploadUpdatedEvent(event, sendOptions)
    } else if (event.meta.delete === true) {
        await Cale_uploadDeletedEvent(event, sendOptions)
    }
    return event
}
